import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, formatDate } from '../../utils/api'
import { MetricsRow } from '../shared'
import ReviewProposal from './ReviewProposal'
import '../../styles/pages.css'

/**
 * Confidence score badge with color coding
 */
function ConfidenceBadge({ value, label }) {
  const numValue = parseFloat(value) || 0
  let color = 'neutral'
  if (numValue >= 0.85) color = 'success'
  else if (numValue >= 0.7) color = 'warning'
  else if (numValue >= 0.5) color = 'caution'
  else color = 'error'

  return (
    <span
      className={`badge badge-${color}`}
      title={label ? `${label}: ${(numValue * 100).toFixed(0)}%` : undefined}
    >
      {(numValue * 100).toFixed(0)}%
    </span>
  )
}

function PIMReview() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Stats
  const [stats, setStats] = useState({
    needs_review: 0,
    accepted: 0,
    dismissed: 0
  })

  // Proposals list
  const [proposals, setProposals] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')

  // Selected proposal for detail view
  const [selectedProposal, setSelectedProposal] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    loadStats()
    loadProposals()
  }, [])

  useEffect(() => {
    loadProposals()
  }, [page, statusFilter])

  async function loadStats() {
    try {
      const response = await api.get('/api/pim/review/stats')
      setStats(response)
    } catch (err) {
      console.error('Failed to load review stats:', err)
    }
  }

  async function loadProposals() {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      params.set('page', page.toString())
      params.set('page_size', pageSize.toString())

      const response = await api.get(`/api/pim/review?${params}`)
      setProposals(response.items || [])
      setTotal(response.total || 0)
    } catch (err) {
      console.error('Failed to load proposals:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectProposal(proposal) {
    setDetailLoading(true)
    try {
      const response = await api.get(`/api/pim/review/${proposal.proposal_id}`)
      setSelectedProposal(response)
    } catch (err) {
      console.error('Failed to load proposal detail:', err)
      setError(err.message)
    } finally {
      setDetailLoading(false)
    }
  }

  async function handleAccept(proposalId) {
    try {
      await api.post(`/api/pim/review/${proposalId}/accept`)
      setSelectedProposal(null)
      loadStats()
      loadProposals()
    } catch (err) {
      console.error('Failed to accept proposal:', err)
      setError(err.message)
    }
  }

  async function handleDismiss(proposalId) {
    try {
      await api.post(`/api/pim/review/${proposalId}/dismiss`)
      setSelectedProposal(null)
      loadStats()
      loadProposals()
    } catch (err) {
      console.error('Failed to dismiss proposal:', err)
      setError(err.message)
    }
  }

  function handleCloseDetail() {
    setSelectedProposal(null)
  }

  function handleStatusChange(newStatus) {
    setStatusFilter(newStatus)
    setPage(1)
    if (newStatus) {
      setSearchParams({ status: newStatus })
    } else {
      setSearchParams({})
    }
  }

  const totalPages = Math.ceil(total / pageSize)

  const metrics = [
    { label: 'Needs Review', value: stats.needs_review, color: stats.needs_review > 0 ? 'yellow' : '' },
    { label: 'Accepted', value: stats.accepted, color: 'green' },
    { label: 'Dismissed', value: stats.dismissed, color: 'red' }
  ]

  return (
    <>
      <MetricsRow metrics={metrics} />

      {/* Filters */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <div className="filter-group" style={{ width: 200 }}>
            <label className="filter-label">Status</label>
            <select
              className="form-input"
              value={statusFilter}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="">Needs Review</option>
              <option value="manually_added_after_review">Accepted</option>
              <option value="dismissed_data">Dismissed</option>
            </select>
          </div>
          {total > 0 && (
            <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
              {total} proposal{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : proposals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Proposals Found</div>
            <div className="empty-state-description">
              {statusFilter === '' ? 'All items have been reviewed!' : 'No proposals with this status.'}
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Image</th>
                <th>Source Item</th>
                <th>Proposed Brand</th>
                <th>Proposed Model</th>
                <th style={{ textAlign: 'center' }}>Confidence</th>
                <th style={{ width: 100 }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map(proposal => (
                <tr
                  key={proposal.proposal_id}
                  onClick={() => handleSelectProposal(proposal)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    {proposal.source_item?.image_urls?.[0] ? (
                      <img
                        src={proposal.source_item.image_urls[0]}
                        alt={proposal.source_item.title}
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: 'cover',
                          borderRadius: 4,
                          background: 'var(--color-bg-secondary)'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          background: 'var(--color-bg-tertiary)',
                          borderRadius: 4,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--color-text-muted)',
                          fontSize: 12
                        }}
                      >
                        No img
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{proposal.source_item?.title || 'Unknown'}</div>
                    <div className="text-sm text-muted">{proposal.source_item?.item_id}</div>
                  </td>
                  <td>{proposal.proposed_data?.brand || '-'}</td>
                  <td>{proposal.proposed_data?.model || '-'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <ConfidenceBadge value={proposal.overall_confidence} label="Overall" />
                  </td>
                  <td className="text-muted text-sm">
                    {formatDate(proposal.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {proposals.length > 0 && totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--space-3)',
            borderTop: '1px solid var(--color-border)'
          }}>
            <span className="text-sm text-muted">
              Page {page} of {totalPages} ({total} total)
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedProposal && (
        <ReviewProposal
          proposal={selectedProposal}
          loading={detailLoading}
          onAccept={handleAccept}
          onDismiss={handleDismiss}
          onClose={handleCloseDetail}
        />
      )}
    </>
  )
}

export default PIMReview
